import React, { useState, useEffect } from "react";
import axios from "axios";

/*
  Tally/accountant-style Invoice Review (Stock Management)
  Advanced/modern UI — styled for a fast accountant/ledger/Tally workflow:
  - Sidebar with quick summaries & totals
  - Ledger-like table, densely packed but readable
  - Advanced filters with immediate feedback
  - Print/tally-friendly
  - Invoice modal: detailed, print-ready, and familiar to Tally/ERP operators
*/

const API_URL = "https://stockaadvikbackend.aadviktech.com";

interface Invoice {
  id: number;
  farm: string;
  party: string;
  invoice_type: string;
  date: string;
  datainvoice: string;
}

interface DataInvoiceRow {
  id: number;
  stock_name: string;
  unit: string;
  total_amount: string;
  indirect_charges: string;
  perUnitCharge: string;
  category: string;
  investment_type: string;
  total_amount_category: string;
}

interface Party {
  id: number;
  name: string;
  number: number;
  contact_person_name: string;
  contact_person_number: number;
  categories: string;
  date: string;
  total_amount: number;
  deleteparty: number;
}

function toCurrency(val: number | string) {
  const n = Number(val) || 0;
  return n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatDate(dateString?: string) {
  if (!dateString) return "";
  const d = new Date(dateString);
  if (String(d) === "Invalid Date") return dateString;
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}
function numberToWords(num: number): string {
  if (isNaN(num)) return "";
  if (num === 0) return "Zero";
  const a = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine",
    "Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
  ];
  const b = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
  const g = ["", "Thousand", "Lakh", "Crore", "Arab", "Kharab"];
  let s = "";
  const n = Math.floor(num);
  function _inWordsHundred(n: number) {
    let str = "";
    if (n > 99) {
      str += a[Math.floor(n / 100)] + " Hundred ";
      n = n % 100;
    }
    if (n > 19) {
      str += b[Math.floor(n / 10)] + " " + a[n % 10];
    } else {
      str += a[n];
    }
    return str.trim();
  }
  let i = 0;
  let words = [];
  let segment;
  let remaining = n;
  if (remaining === 0) return "Zero";
  while (remaining > 0 && i < g.length) {
    if (i === 0) {
      segment = remaining % 1000;
      remaining = Math.floor(remaining / 1000);
    } else {
      segment = remaining % 100;
      remaining = Math.floor(remaining / 100);
    }
    if (segment)
      words.push(_inWordsHundred(segment) + (g[i] ? " " + g[i] : ""));
    i++;
  }
  s = words.reverse().join(" ").replace(/\s+/g, " ").trim();
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s;
}

// Invoice types for filtering
const INVOICE_TYPES = [
  { key: "ALL", label: "All Types" },
  { key: "Sell", label: "Sell" },
  { key: "Purchase", label: "Purchase" },
];

// Color for badges (Tally-inspired)
const colorBadge = (type: string) => {
  switch (type.toLowerCase()) {
    case "sell":
      return "bg-green-50 text-green-700 border-green-400";
    case "purchase":
      return "bg-yellow-50 text-yellow-800 border-yellow-400";
    default:
      return "bg-slate-100 text-slate-900 border-slate-400";
  }
};

const InvoicePage: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [partiesLoading, setPartiesLoading] = useState<boolean>(false);
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [viewingInvoice, setViewingInvoice] = useState<Invoice | null>(null);

  // For delete confirmation (modal)
  const [deletingInvoice, setDeletingInvoice] = useState<Invoice | null>(null);
  const [deleting, setDeleting] = useState<boolean>(false);

  // Filters
  const [filterType, setFilterType] = useState<string>("ALL");
  const [filterParty, setFilterParty] = useState<string>("ALL");
  const [filterFarm, setFilterFarm] = useState<string>("ALL");
  const [filterStock, setFilterStock] = useState<string>("ALL");
  const [uniqueFarms, setUniqueFarms] = useState<string[]>([]);
  const [uniqueStockNames, setUniqueStockNames] = useState<string[]>([]);

  // Stats
  const [totalSell, setTotalSell] = useState<number>(0);
  const [totalPurchase, setTotalPurchase] = useState<number>(0);

  useEffect(() => {
    fetchParties();
    fetchInvoices();
  }, []);

  useEffect(() => {
    const farmSet = new Set<string>();
    const stockSet = new Set<string>();
    let sell = 0, purchase = 0;
    invoices.forEach(inv => {
      if (inv.farm && inv.farm.trim() !== "") farmSet.add(inv.farm.trim());
      try {
        const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
        rows.forEach(row => {
          if (row.stock_name && row.stock_name.trim() !== "") stockSet.add(row.stock_name.trim());
          if (inv.invoice_type.toLowerCase() === "sell")
            sell += Number(row.total_amount || 0);
          if (inv.invoice_type.toLowerCase() === "purchase")
            purchase += Number(row.total_amount || 0);
        });
      } catch {}
    });
    setUniqueFarms(Array.from(farmSet));
    setUniqueStockNames(Array.from(stockSet));
    setTotalSell(sell);
    setTotalPurchase(purchase);
  }, [invoices]);

  function fetchParties() {
    setPartiesLoading(true);
    axios
      .get(`${API_URL}/auth/addparty`)
      .then(res => {
        setParties(Array.isArray(res.data) ? res.data as Party[] : []);
      })
      .catch(() => setParties([]))
      .finally(() => setPartiesLoading(false));
  }

  function fetchInvoices() {
    setLoading(true);
    setErrorMsg("");
    axios
      .get(`${API_URL}/auth/invoice`)
      .then(res => {
        setInvoices(Array.isArray(res.data) ? res.data as Invoice[] : []);
        if (!Array.isArray(res.data)) setErrorMsg("Invalid data received");
      })
      .catch(() => setErrorMsg("Failed to fetch invoices"))
      .finally(() => setLoading(false));
  }

  function handleViewModal(invoice: Invoice) {
    setViewingInvoice(invoice);
  }
  function handleCloseModal() {
    setViewingInvoice(null);
  }
  function getPartyName(partyId: string | number | undefined): string {
    if (!partyId) return "";
    const party = parties.find((p) => String(p.id) === String(partyId));
    return party ? party.name : partyId ? `ID#${partyId}` : "";
  }

  // Delete Invoice Functions
  function openDeleteDialog(invoice: Invoice) {
    setDeletingInvoice(invoice);
  }
  function closeDeleteDialog() {
    setDeletingInvoice(null);
  }
  function handleDeleteInvoice(invoiceId: number) {
    setDeleting(true);
    setErrorMsg("");
    axios
      .delete(`${API_URL}/auth/invoice/${invoiceId}`)
      .then(res => {
        setInvoices(invoices => invoices.filter(inv => inv.id !== invoiceId));
        closeDeleteDialog();
        if (viewingInvoice && viewingInvoice.id === invoiceId) {
          setViewingInvoice(null);
        }
      })
      .catch(err => {
        setErrorMsg("Failed to delete invoice.");
      })
      .finally(() => setDeleting(false));
  }

  // Print modal (print only the invoice contents)
  const handlePrint = () => {
    const invoicePrintElem = document.getElementById("invoice-print");
    if (!invoicePrintElem) return;
    const printContents = invoicePrintElem.innerHTML;
    const printWindow = window.open("", "", "width=900,height=700");
    if (printWindow) {
      printWindow.document.write(`
        <html>
        <head>
          <title>Invoice</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 4px 7px !important; background: #f7fafb; color: #222; font-size: 11.5px !important; margin: 0; line-height: 1.25; max-width: 830px; width: 100vw; box-sizing: border-box; }
            .text-xs { font-size: 10px !important; }
            .font-semibold { font-weight: 600; }
            .font-bold { font-weight: 700; }
            .font-extrabold { font-weight: 800; }
            .text-lg { font-size: 14px !important; }
            .text-xl { font-size: 15px !important; }
            .text-green-700 { color: #15803d; }
            .text-blue-900 { color: #1e3a8a;}
            .text-blue-800 { color: #1e40af;}
            .text-blue-700 { color: #1d4ed8;}
            .text-gray-700 { color: #374151;}
            .text-gray-800 { color: #1f2937;}
            .italic { font-style: italic; }
            .bg-blue-50 { background: #eff6ff; }
            .bg-blue-100 { background: #dbeafe; }
            .bg-gradient-to-tr {
              background: linear-gradient(135deg, #fff 0%, #eaeffe 40%, #f0f7ff 100%);
            }
            .bg-gradient-to-t { background: linear-gradient(0deg, #eaeffe 0%, #e8f5fe 100%);}
            .bg-gradient-to-l { background: linear-gradient(270deg, #ffe4ef 0%, #fff 100%);}
            .bg-gray-50 { background: #f9fafb;}
            .bg-white { background: #fff;}
            .mb-1 { margin-bottom: 2px !important;}
            .ml-8 { margin-left: 16px !important;}
            .mb-2 { margin-bottom: 6px !important;}
            .mb-4 { margin-bottom: 10px !important;}
            .mb-5 { margin-bottom: 11px !important;}
            .mb-6 { margin-bottom: 13px !important;}
            .mt-1 { margin-top: 2px !important;}
            .mt-2 { margin-top: 4px !important;}
            .mt-4 { margin-top: 9px !important;}
            .mt-5 { margin-top: 10px !important;}
            .rounded-2xl { border-radius: 0.65rem;}
            .rounded-xl { border-radius: 0.4rem;}
            .rounded-lg { border-radius: 0.25rem;}
            .rounded-bl-lg { border-bottom-left-radius: 0.25rem;}
            .rounded-br-lg { border-bottom-right-radius: 0.25rem;}
            .border { border: 1px solid #cbd5e1;}
            .border-blue-100 { border-color: #dbeafe;}
            .border-blue-200 { border-color: #bfdbfe;}
            .border-blue-300 { border-color: #93c5fd;}
            .border-gray-200 { border-color: #e5e7eb;}
            .shadow, .shadow-lg, .shadow-2xl, .shadow-xl { box-shadow: none !important;}
            .table-title { background: #2062b8; color: #fff; font-weight: 600;}
            .invoice-total { background: #e9fbe8 !important; font-size: 13px !important;}
            .w-full { width: 100%;}
            .w-1\\/2 { width: 50%; }
            .align-top { vertical-align: top;}
            .text-right { text-align: right;}
            .text-center { text-align: center;}
            .flex { display: flex;}
            .flex-col { flex-direction: column;}
            .flex-row { flex-direction: row;}
            .items-center { align-items: center;}
            .items-end { align-items: flex-end;}
            .text-end { text-align: end;}
            .justify-between { justify-content: space-between;}
            .justify-end { justify-content: flex-end;}
            .justify-center { justify-content: center;}
            .gap-5 { gap: 10px;}
            .gap-6 { gap: 15px;}
            .overflow-auto { overflow: auto;}
            .overflow-x-auto { overflow-x: auto;}
            .h-12 { height: 36px !important;}
            .p-2 {padding:5px!important;} 
            .p-3 { padding: 7px !important;}
            .p-4 { padding: 7px !important;}
            .p-6 { padding: 14px !important;}
            .py-1 { padding-top: 2px !important; padding-bottom: 2px !important;}
            .py-2 { padding-top: 5px !important; padding-bottom: 5px !important;}
            .py-3 { padding-top: 7px !important; padding-bottom: 7px !important;}
            .px-2 { padding-left: 5px !important; padding-right: 5px !important;}
            .px-3 { padding-left: 8px !important; padding-right: 8px !important;}
            .px-6 { padding-left: 14px !important; padding-right: 14px !important;}
            .border-collapse { border-collapse: collapse;}
            .border-l-0 { border-left: 0;}
            .border-r { border-right: 1px solid #bfdbfe;}
            #invoice-print { max-height: 99vh; overflow: hidden; }
            /* Added styling for Tax Invoice heading */
            .tax-invoice-heading {
              text-align: center;
              font-size: 22px;
              font-weight: 800;
              letter-spacing: 1px;
              color: #1e3a8a;
              padding-top: 24px;
              padding-bottom: 18px;
            }
            @media print {
              html, body {
                width: 100vw;
                max-width: 856px !important;
                margin: 0 auto !important;
                padding: 0 !important;
                overflow: hidden !important;
                font-size: 11.5px !important;
              }
              #invoice-print {
                padding: 0 6px !important;
                margin: 0 !important;
                width: 100% !important;
                max-width: 848px !important;
                max-height: 99vh !important;
                overflow: hidden !important;
              }
              .shadow, .shadow-lg, .shadow-xl, .shadow-2xl { box-shadow: none !important;}
              .bg-gradient-to-tr, .bg-gradient-to-t, .bg-gradient-to-l, .bg-gray-50, .bg-blue-50, .bg-blue-100, .bg-white { background: #fff !important; }
              tr, td, th, table, div, span, section {
                page-break-inside: avoid !important;
                page-break-after: auto !important;
                page-break-before: auto !important;
              }
              table, tbody, thead, tfoot, tr, td, th {
                margin: 0 !important;
                padding: 0 !important;
              }
              .tax-invoice-heading {
                text-align: center;
                font-size: 22px;
                font-weight: 800;
                letter-spacing: 1px;
                color: #1e3a8a;
                padding-top: 24px;
                padding-bottom: 18px;
              }
            }
          </style>
        </head>
        <body>
          ${printContents.replace(
            /^(\s*)<div[^>]*id="?invoice-print"?[^>]*>/i,
            ""
          ).replace(/<\/div>\s*$/, "")}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 350);
      };
    }
  };

  // Main filter logic
  const filteredInvoices = React.useMemo(() => {
    let filtered = invoices;
    if (filterType !== "ALL") {
      filtered = filtered.filter(
        inv => inv.invoice_type && inv.invoice_type.trim().toLowerCase() === filterType.toLowerCase()
      );
    }
    if (filterParty !== "ALL") {
      filtered = filtered.filter(inv => String(inv.party) === String(filterParty));
    }
    if (filterFarm !== "ALL") {
      filtered = filtered.filter(inv => inv.farm && inv.farm.trim() === filterFarm);
    }
    if (filterStock !== "ALL") {
      filtered = filtered.filter(inv => {
        try {
          const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
          return rows.some(row => row.stock_name && row.stock_name.trim() === filterStock);
        } catch { return false; }
      });
    }
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      filtered = filtered.filter((inv) => {
        try {
          const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
          return (
            (inv.farm || "").toLowerCase().includes(q) ||
            (getPartyName(inv.party) ?? "").toLowerCase().includes(q) ||
            (inv.invoice_type || "").toLowerCase().includes(q) ||
            rows.some(row =>
              (row.stock_name ?? "").toLowerCase().includes(q) ||
              (row.category ?? "").toLowerCase().includes(q) ||
              (row.investment_type ?? "").toLowerCase().includes(q)
            )
          );
        } catch { return false; }
      });
    }
    return filtered;
  }, [invoices, filterType, filterParty, filterFarm, filterStock, searchText, parties]);

  // --- UI ---
  return (
    <div className="h-screen flex bg-gradient-to-br from-slate-100 via-blue-50 to-slate-200">
      {/* SIDEBAR: summary like an accountant's quick panel */}
      <div className="w-[260px] min-w-[190px] max-w-xs border-r bg-gradient-to-br from-green-50 via-white to-blue-100 flex flex-col px-4 py-6 shadow-sm gap-7 relative">
        <div>
          <div className="text-xl font-extrabold text-green-950 tracking-tight mb-2 ">Invoice Dashboard</div>
          <div className="text-[12.8px] text-gray-600 mb-2">
            {/* <span className="font-bold text-blue-700">Welcome, Tally Operator!</span> */}
          </div>
        </div>
        <div className="flex flex-col gap-3">
          <div className="p-3 rounded border-l-4 border-blue-500 bg-blue-50">
            <div className="text-[12px] text-blue-700 uppercase tracking-wide font-semibold">Total Sell</div>
            <div className="text-blue-900 text-xl font-extrabold">₹ {toCurrency(totalSell)}</div>
          </div>
          <div className="p-3 rounded border-l-4 border-yellow-500 bg-yellow-50">
            <div className="text-[12px] text-yellow-700 uppercase tracking-wide font-semibold">Total Purchase</div>
            <div className="text-yellow-900 text-xl font-extrabold">₹ {toCurrency(totalPurchase)}</div>
          </div>
          <div className="p-3 rounded border-l-4 border-gray-400 bg-gray-50">
            <div className="text-[12px] text-gray-600 uppercase tracking-wide font-semibold">Invoices</div>
            <div className="text-gray-900 text-xl font-extrabold">{filteredInvoices.length}</div>
          </div>
        </div>
        <div className="flex-1" />
        <div className="pt-8">
          <div className="text-[11.5px] text-gray-400 font-mono leading-relaxed">
            Tip: Filter like a pro—try part of name or date!
            <br />
            Use <span className="bg-blue-100 px-2 py-0.5 rounded">View</span> for details.
          </div>
          <div className="absolute bottom-2 left-4 right-4 text-slate-300 font-mono text-[10.6px] select-none">Stock Aadvik Ledger · Tally UI</div>
        </div>
      </div>

      {/* Main workspace: Filters + Table */}
      <div className="flex-1 flex flex-col min-w-0 h-full">
        {/* Header & Filters */}
        <div className="flex flex-col md:flex-row items-center justify-between px-7 pt-7 pb-3 bg-gradient-to-br from-white to-blue-100 border-b gap-4">
          <div>
            <div className="text-xl font-extrabold text-blue-900 tracking-tight mb-1 leading-7">Stock Invoice Ledger</div>
            <div className="text-gray-500 text-[12.3px]">Accountant-style—Review, tally and print quickly.</div>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
            {/* Search */}
            <input
              type="text"
              className="px-2.5 py-2 rounded-lg border border-gray-200 bg-white shadow focus:outline-blue-400 w-[180px] text-[13.1px]"
              placeholder="Search (Party, Stock, Farm, Type)..."
              value={searchText}
              onChange={e => setSearchText(e.target.value)}
              style={{ minWidth: 0 }}
            />
            {/* Invoice Type Filter */}
            <select
              value={filterType}
              onChange={e => setFilterType(e.target.value)}
              className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[105px]"
            >
              {INVOICE_TYPES.map(opt => (<option key={opt.key} value={opt.key}>{opt.label}</option>))}
            </select>
            {/* Party Filter */}
            <select
              value={filterParty}
              onChange={e => setFilterParty(e.target.value)}
              className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[140px]"
            >
              <option value="ALL">All Parties</option>
              {parties.map(party => (
                <option key={party.id} value={party.id}>{party.name}</option>
              ))}
            </select>
            {/* Farm Filter */}
            <select
              value={filterFarm}
              onChange={e => setFilterFarm(e.target.value)}
              className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[110px]"
            >
              <option value="ALL">All Farms</option>
              {uniqueFarms.map((farm, idx) => (
                <option key={farm + idx} value={farm}>{farm}</option>
              ))}
            </select>
            {/* Stock Filter */}
            <select
              value={filterStock}
              onChange={e => setFilterStock(e.target.value)}
              className="px-2 py-2 rounded-lg border border-blue-200 bg-white text-blue-800 text-sm w-[150px]"
            >
              <option value="ALL">All Stocks</option>
              {uniqueStockNames.map((stock, idx) => (
                <option key={stock + idx} value={stock}>{stock}</option>
              ))}
            </select>
            {/* Reset */}
            <button
              className="ml-1 px-4 py-2 rounded-lg font-semibold border text-sm transition bg-gray-50 text-blue-800 border-gray-300 hover:bg-blue-50 hover:text-blue-900"
              onClick={() => {
                setFilterType("ALL");
                setFilterParty("ALL");
                setFilterFarm("ALL");
                setFilterStock("ALL");
                setSearchText("");
              }}
              type="button"
            >
              Reset
            </button>
          </div>
        </div>

        {/* Error message */}
        {errorMsg && (
          <div className="mb-4 p-2 rounded border text-center shadow bg-red-50 text-red-700 border-red-200 mx-7">
            {errorMsg}
          </div>
        )}

        {/* Ledger Table */}
        <div className="h-full overflow-y-auto px-3 pt-3 pb-8 flex-1">
          <div className="rounded-2xl bg-white/90 border border-blue-200 shadow-2xl overflow-auto">
            <table className="min-w-[1200px] w-full table-auto border-separate border-spacing-y-2">
              <thead className="sticky top-0 bg-gradient-to-br from-blue-50 via-white to-gray-100 shadow z-10">
                <tr className="text-[15px] font-bold tracking-tight text-blue-900 border-b-2 border-blue-400/30">
                  <th className="py-3 px-4 text-center">#</th>
                  <th className="py-3 px-4 text-center">Date</th>
                  <th className="py-3 px-4 text-center">Type</th>
                  <th className="py-3 px-4 text-center">Farm</th>
                  <th className="py-3 px-4 text-center">Party</th>
                  <th className="py-3 px-4 text-center">Stock(s)</th>
                  <th className="py-3 px-4 text-center">Amount (₹)</th>
                  <th className="py-3 px-4 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(loading || partiesLoading) ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-400 text-lg">Loading...</td>
                  </tr>
                ) : filteredInvoices.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-12 text-center text-gray-400 text-lg">No invoices found.</td>
                  </tr>
                ) : (
                  filteredInvoices.map((inv, idx) => {
                    let totalAmt = 0, stockString = "";
                    try {
                      const rows: DataInvoiceRow[] = JSON.parse(inv.datainvoice);
                      totalAmt = rows.reduce((sum, row) => sum + Number(row.total_amount || 0), 0);
                      stockString = rows.map(row => row.stock_name).filter(Boolean).join(", ");
                    } catch {}
                    return (
                      <tr
                        key={inv.id}
                        className="hover:bg-yellow-50/70 even:bg-blue-50/30 transition-all border border-slate-100 rounded-xl"
                        style={{ cursor: "pointer" }}
                        tabIndex={0}
                        onDoubleClick={() => handleViewModal(inv)}
                      >
                        <td className="text-blue-900 text-center font-mono py-3 px-4">{idx + 1}</td>
                        <td className="text-center py-3 px-4">{formatDate(inv.date)}</td>
                        <td className="text-center py-3 px-4">
                          <span className={`inline-block px-2 py-0.5 rounded font-semibold border text-xs ${colorBadge(inv.invoice_type)}`}>
                            {inv.invoice_type}
                          </span>
                        </td>
                        <td className="text-center py-3 px-4">{inv.farm}</td>
                        <td className="text-center py-3 px-4">{getPartyName(inv.party)}</td>
                        <td className="text-center truncate max-w-xs py-3 px-4" title={stockString}>
                          {stockString || "—"}
                        </td>
                        <td className="text-center font-mono text-blue-900 font-bold py-3 px-4">
                          ₹ {toCurrency(totalAmt)}
                        </td>
                        <td className="text-center flex justify-center gap-2 py-3 px-4">
                          <button
                            className="px-3 py-1.5 bg-gradient-to-r from-blue-400 to-blue-600 rounded-lg text-white text-xs font-bold hover:from-blue-600 hover:to-blue-900 transition shadow"
                            title="View Invoice"
                            onClick={e => {
                              e.stopPropagation();
                              handleViewModal(inv);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="px-3 py-1.5 bg-gradient-to-r from-red-500 to-red-700 rounded-lg text-white text-xs font-bold hover:from-red-700 hover:to-red-800 transition shadow"
                            title="Delete Invoice"
                            onClick={e => {
                              e.stopPropagation();
                              openDeleteDialog(inv);
                            }}
                          >
                            Delete
                          </button>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {deletingInvoice && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/40"></div>
          <div className="relative bg-white rounded-xl shadow-lg p-8 text-center max-w-sm w-full border border-red-200">
            <div className="text-lg font-bold text-red-800 mb-2">
              Delete Invoice
            </div>
            <div className="text-gray-800 mb-4">
              Are you sure you want to delete invoice <b>#{deletingInvoice.id}</b>?<br />
              This action cannot be undone.
            </div>
            <div className="flex items-center justify-center gap-4">
              <button
                className="px-4 py-2 rounded-lg bg-gray-300 text-gray-800 font-semibold hover:bg-gray-400 transition"
                onClick={closeDeleteDialog}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 rounded-lg bg-red-600 text-white font-bold hover:bg-red-800 transition"
                onClick={() => handleDeleteInvoice(deletingInvoice.id)}
                disabled={deleting}
              >
                {deleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Invoice Modal (Popup) */}
      {viewingInvoice && (
        <div className="fixed inset-0 z-50 flex flex-col h-screen w-screen">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40"></div>
          {/* Modal */}
          <div className="relative flex flex-col flex-1 justify-center items-center h-full w-full py-7 px-2">
            <div className="bg-gradient-to-tr from-white to-blue-50 w-full max-w-[900px] h-full max-h-[97vh] flex flex-col overflow-hidden rounded-2xl shadow-2xl border border-blue-200">
              {/* Top bar with actions */}
              <div className="flex justify-end gap-5 py-3 px-4 flex-shrink-0 bg-gradient-to-r from-blue-200/20 via-blue-100/5 to-white rounded-t-2xl border-b border-blue-100">
                <button
                  onClick={handlePrint}
                  className="bg-gradient-to-r from-blue-600 to-blue-400 text-white px-3 py-2 rounded-xl shadow hover:from-blue-400 hover:to-blue-200 transition text-[13px]"
                >
                  Print
                </button>
                <button
                  onClick={() => viewingInvoice && openDeleteDialog(viewingInvoice)}
                  className="bg-gradient-to-r from-red-500 to-red-700 text-white px-3 py-2 rounded-xl shadow hover:from-red-800 hover:to-red-900 transition text-[13px]"
                >
                  Delete
                </button>
                <button
                  onClick={handleCloseModal}
                  className="bg-gray-500 hover:bg-gray-800 text-white px-3 py-2 rounded-xl shadow transition text-[13px]"
                >
                  Close
                </button>
              </div>
              {/* Modal Content - Scrollable, fit rest height */}
              <div
                id="invoice-print"
                className="p-2 text-[11.5px] text-gray-800 overflow-scroll"
              >
                {/* Tax Invoice Heading */}
                <div
                  className="text-center py-4"
                  style={{
                    fontSize: "22px",
                    fontWeight: 800,
                    letterSpacing: "1px",
                    color: "#1e3a8a",
                    paddingTop: "24px",
                    paddingBottom: "18px",
                  }}
                >
                  INVOICE
                </div>
                <table className="w-full border border-blue-300 border-collapse rounded-2xl shadow mb-2">
                  <tbody>
                    <tr className="invoice__header">
                      <td className="border p-2 w-1/2 align-top">
                        <div
                          className="mb-1 text-xl text-blue-900 font-extrabold tracking-wide"
                          style={{
                            fontSize: "14.5px",
                            lineHeight: "1.2",
                          }}
                        >
                          {viewingInvoice.farm}
                        </div>
                        {/* <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Address:</b> Address here
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>GSTIN:</b> GSTIN here
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>State:</b> State here
                        </div>
                        <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>CIN:</b> CIN here
                        </div> */}
                        {/* <div
                          className="mb-1 text-gray-700"
                          style={{ fontSize: "11.5px" }}
                        >
                          <b>Email:</b> company@email.com
                        </div> */}
                      </td>
                      <td className="border p-2 align-top bg-gradient-to-t from-blue-50 to-blue-100">
                        <div className="grid grid-cols-2 gap-4 w-full">
                        
                        <div
                          className="mb-1 flex flex-col"
                          style={{ fontSize: "12px", minWidth: "135px" }}
                        >
                          <b>Invoice No:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.id}
                          </span>
                        </div>
                        <div
                          className="mb-1 flex flex-col ml-8"
                          style={{ fontSize: "12px", minWidth: "110px" }}
                        >
                          <b>Dated:</b>{" "}
                          <span className="text-blue-700">
                            {viewingInvoice.date ? formatDate(viewingInvoice.date) : ""}
                          </span>
                        </div>

                        <div
                          className="mb-1 flex flex-col"
                          style={{ fontSize: "12px", minWidth: "135px" }}
                        >
                          <b>Supplier Ref.:</b>{" "}
                          <span className="text-blue-700">
                            
                          </span>
                        </div>
                        <div
                          className="mb-1 flex flex-col ml-8"
                          style={{ fontSize: "12px", minWidth: "110px" }}
                        >
                          <b>Other Ref. :</b>{" "}
                          <span className="text-blue-700">
                            
                          </span>
                        </div>
                        </div>
                        
                      </td>
                    </tr>
                    <tr>
                      <td className="border p-2 align-top bg-gradient-to-l from-pink-50 to-white/90" style={{ fontSize: '11.5px' }}>
                        <b className="text-blue-800">Consignee</b>
                        <div className="" />
                        <div className="mb-1 mt-1">
                          <b>{getPartyName(viewingInvoice.party)}</b>
                        </div>
                        {/* <div className="mb-1"><b>City:</b> —</div>
                        <div className="mb-1"><b>State:</b> —</div>
                        <div className="mb-1"><b>GSTIN:</b> —</div> */}
                      </td>
                      <td className="border p-2 bg-white"></td>
                    </tr>
                  </tbody>
                </table>

                {/* Table of Invoice Items */}
                <table
                  className="w-full border border-black border-collapse mt-[2px] mb-2 rounded-lg"
                  style={{
                    fontSize: "11.5px",
                    borderWidth: "1.2px",
                  }}
                >
                  <thead>
                    <tr style={{ height: "22px", fontSize: "11.7px" }}>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                        }}
                      >
                        Description of Goods
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "70px",
                        }}
                      >
                        Quantity
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "90px",
                        }}
                      >
                        Per Unit Price
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "70px",
                        }}
                      >
                        Per
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "75px",
                        }}
                      >
                        Disc %
                      </th>
                      <th
                        className="border border-black"
                        style={{
                          padding: "3px 6px",
                          textAlign: "center",
                          fontWeight: 600,
                          width: "110px",
                        }}
                      >
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(() => {
                      let rows: DataInvoiceRow[] = [];
                      let totalUnits = 0;
                      let totalAmount = 0;
                      try { rows = JSON.parse(viewingInvoice.datainvoice); } catch {}
                      if (!Array.isArray(rows) || rows.length === 0)
                        return (
                          <tr>
                            <td colSpan={6} className="py-4 text-center text-gray-400 italic font-semibold">
                              No data rows in invoice
                            </td>
                          </tr>
                        );
                      // Compute totals
                      totalUnits = rows.reduce((sum, r) => sum + Number(r.unit || 0), 0);
                      totalAmount = rows.reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
                      return (
                        <>
                          {rows.map((r, i) => (
                            <tr key={i}>
                              <td
                                className="border border-black align-top"
                                style={{
                                  padding: "7px 10px 24px 7px",
                                  fontSize: "12.4px",
                                  minHeight: "90px",
                                }}
                              >
                                <div>
                                  <div style={{ whiteSpace: "pre-line" }}>
                                    {r.stock_name}
                                  </div>
                                </div>
                              </td>
                              <td
                                className="border border-black align-top text-center"
                                style={{
                                  padding: "7px 8px",
                                  fontSize: "12.6px",
                                  minWidth: "64px",
                                }}
                              >
                                {typeof r.unit !== 'undefined' && r.unit !== null && r.unit !== ""
                                  ? r.unit + " NOS"
                                  : "-"}
                              </td>
                              <td
                                className="border border-black align-top text-right"
                                style={{
                                  padding: "7px 8px",
                                  fontSize: "12.6px",
                                  fontFamily: "inherit",
                                  minWidth: "84px",
                                }}
                              >
                                {r.perUnitCharge ? toCurrency(r.perUnitCharge) : "-"}
                              </td>
                              <td
                                className="border border-black align-top text-center"
                                style={{
                                  padding: "7px 8px",
                                  fontSize: "12.6px",
                                  minWidth: "52px",
                                }}
                              >
                                NOS
                              </td>
                              <td
                                className="border border-black align-top text-right"
                                style={{
                                  padding: "7px 8px",
                                  fontSize: "12.6px",
                                  minWidth: "55px",
                                }}
                              >
                                {"-"}
                              </td>
                              <td
                                className="border border-black align-top text-right"
                                style={{
                                  padding: "7px 8px",
                                  fontSize: "12.6px",
                                  fontFamily: "inherit",
                                  minWidth: "84px",
                                }}
                              >
                                {toCurrency(r.total_amount)}
                              </td>
                            </tr>
                          ))}
                          <tr className="font-bold bg-green-50 invoice-total">
                            <td className="border p-2 text-right" colSpan={1}>
                              Total
                            </td>
                            <td className="border p-2 text-center text-green-700 text-[15px] font-bold">
                              {totalUnits}
                            </td>
                            <td className="border p-2 text-center" colSpan={3}></td>
                            <td className="border p-2 text-right text-green-700 text-[16px] font-bold">
                              ₹{toCurrency(totalAmount)}
                            </td>
                          </tr>
                        </>
                      );
                    })()}
                  </tbody>
                </table>
                {/* Amount in Words */}
                <div className="w-full border border-gray-200 rounded-lg bg-gray-50 px-2 py-2 mb-2 flex flex-col">
                  <span
                    className="text-lg font-bold text-blue-900 mb-1"
                    style={{ fontSize: "12.5px" }}
                  >
                    Amount Chargeable (in words)
                  </span>
                  <span className="text-[11.5px] text-gray-700 font-mono">
                    {(() => {
                      let total = 0;
                      try {
                        const rows: DataInvoiceRow[] = JSON.parse(viewingInvoice.datainvoice);
                        total = rows.reduce((sum, r) => sum + Number(r.total_amount || 0), 0);
                      } catch {}
                      return numberToWords(total) + " Rupees only.";
                    })()}
                  </span>
                </div>
                {/* Declaration / Remarks and Bank Details */}
                <div className="w-full border border-blue-200 border-collapse mt-[4px] rounded-lg overflow-hidden flex flex-row items-stretch bg-white">
                  <div
                    className="w-1/2 border-r border-blue-200 p-2 flex flex-col justify-between bg-gray-50"
                    style={{ fontSize: "11px" }}
                  >
                    <div>
                      <b className="text-blue-900 mb-1 block">Declaration</b>
                      <div className="text-gray-800 mt-1">
                        We declare that this invoice shows the actual price of the goods described and that all particulars are true and correct.
                      </div>
                    </div>
                  </div>
                  <div className="w-1/2 p-2 flex flex-row justify-between text-left bg-white">
                    <div className="flex flex-col w-full border border-blue-200 border-collapse mt-1 rounded-lg overflow-hidden">
                      <div className="border p-2 w-full align-top bg-gray-50 rounded-bl-lg flex flex-col" style={{ fontSize: '11px' }}>
                       
                        <div className="mt-1 mb-1">Company Sales Tax No.</div>
                        <div>Buyer's Sales Tax No. <span className="font-semibold"></span></div>
                        <div>Buyer's Inter State Sales Tax No.<span className="font-semibold"></span></div>
                       
                      </div>
                      <div className="border border-l-0 p-2 w-full text-right align-top bg-white rounded-br-lg flex flex-col justify-between">
                        <div>
                          <div className="mb-2">For <b>{viewingInvoice.farm}</b></div>
                          {/* Signature */}
                          <div className="text-gray-400 italic text-sm text-center" style={{ fontSize: '10px' }}>
                            No signature on file
                          </div>
                          <div className="pt-1 font-semibold" style={{ fontSize: '10.5px' }}>Authorised Signatory</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvoicePage;